import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { StarRating, StarPicker } from "@/components/StarRating";
import { Loader2, Send, Quote, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

const initialForm = { customer_name: "", location: "", rating: 5, title: "", body: "", job_ref: "" };

const ReviewsPage = () => {
  const [data, setData] = useState({ items: [], average_rating: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/reviews");
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.body.trim().length < 10) {
      toast.error("Tell us a bit more — at least 10 characters please");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", { ...form, customer_name: form.customer_name.trim(), body: form.body.trim() });
      toast.success("Thanks! Your review will appear once approved.");
      setForm(initialForm);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />
      <header className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow" data-testid="reviews-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <p className="label-mono text-spark-yellow mb-2">From the bench</p>
          <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">
            Customer reviews
          </h1>
          <div className="mt-5 flex items-center gap-4 flex-wrap">
            <StarRating value={Math.round(data.average_rating)} size={22} />
            <span className="font-display uppercase tracking-tight text-2xl font-bold text-spark-yellow">{data.average_rating.toFixed(1)}</span>
            <span className="text-slate-300 text-sm">based on {data.total_count} review{data.total_count === 1 ? "" : "s"}</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid lg:grid-cols-5 gap-8">
        {/* Reviews list */}
        <section className="lg:col-span-3 space-y-4">
          {loading ? (
            <div className="text-center py-20 text-spark-slate"><Loader2 className="size-6 animate-spin inline" /></div>
          ) : data.items.length === 0 ? (
            <div className="surface-card p-10 text-center" data-testid="reviews-empty">
              <Quote className="size-10 text-spark-line mx-auto mb-3" />
              <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Be the first</p>
              <p className="text-sm text-spark-slate mt-1">No reviews yet — share your repair experience and help others find us.</p>
            </div>
          ) : (
            data.items.map((r) => (
              <article key={r.id} className={`surface-card p-5 sm:p-6 ${r.featured ? "border-l-4 border-l-spark-yellow" : ""}`} data-testid={`review-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <StarRating value={r.rating} size={16} />
                    {r.title && <h3 className="font-display uppercase tracking-tight text-lg font-bold text-spark-ink mt-2">{r.title}</h3>}
                  </div>
                  {r.featured && (
                    <span className="bg-spark-yellow text-spark-ink text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm">Featured</span>
                  )}
                </div>
                <p className="text-spark-ink leading-relaxed mt-3 whitespace-pre-line">{r.body}</p>
                <p className="text-xs text-spark-slate mt-4 flex items-center gap-2">
                  <span className="font-display uppercase tracking-wider font-bold text-spark-ink">{r.customer_name}</span>
                  {r.location && <span>· {r.location}</span>}
                  <span>· {new Date(r.created_at).toLocaleDateString("en-NZ", { month: "short", year: "numeric" })}</span>
                </p>
              </article>
            ))
          )}
        </section>

        {/* Submit form */}
        <aside className="lg:col-span-2 lg:sticky lg:top-24 self-start">
          <div className="surface-card p-6 sm:p-7" data-testid="review-form">
            <p className="label-mono text-spark-teal flex items-center gap-2"><MessageSquarePlus className="size-3.5" /> Leave a review</p>
            <h2 className="font-display uppercase tracking-tight text-2xl font-bold text-spark-ink mt-2">Share your experience</h2>
            <p className="text-sm text-spark-slate mt-1.5">It only takes a minute and helps fellow scooter riders.</p>
            {submitted ? (
              <div className="mt-5 p-5 rounded-md bg-spark-yellow text-spark-ink" data-testid="review-submitted">
                <p className="font-display uppercase tracking-wider font-bold">Thanks!</p>
                <p className="text-sm mt-1">Your review is in moderation and will appear here once approved.</p>
                <button onClick={() => setSubmitted(false)} className="text-xs underline mt-3 font-bold uppercase tracking-wider">Leave another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div>
                  <label className="label-mono mb-2 inline-block">Your rating</label>
                  <div><StarPicker value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} /></div>
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Your name</label>
                  <input className="input-style" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="First name + last initial e.g. Jane S." data-testid="review-name" />
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Where are you (optional)</label>
                  <input className="input-style" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Levin, NZ" />
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Headline (optional)</label>
                  <input className="input-style" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Saved my commute" data-testid="review-title" />
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Your review</label>
                  <textarea className="input-style resize-none" rows={5} required minLength={10} maxLength={2000} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Tell us what you brought in, how it went, and how the scooter is now…" data-testid="review-body" />
                  <p className="text-[10px] text-spark-slate mt-1 text-right">{form.body.length}/2000</p>
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Job ref (optional)</label>
                  <input className="input-style" value={form.job_ref} onChange={(e) => setForm({ ...form, job_ref: e.target.value.toUpperCase() })} placeholder="SPK-26-0000" />
                </div>
                <button type="submit" disabled={submitting} className="btn-accent w-full" data-testid="review-submit">
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Submit review
                </button>
                <p className="text-[10px] text-spark-slate italic">Reviews are moderated to filter spam — yours will be live within a day.</p>
              </form>
            )}
          </div>
        </aside>
      </main>
      <Footer />
    </div>
  );
};

export default ReviewsPage;
